import React from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { usePresentationMode } from "./PresentationContext";
import HealthPill from "./HealthPill";
import NoteableItem from "./NoteableItem";
import { hexToRgba } from "./colorUtils";

function Column({ label, items, accent, numbered, section, product, showPills, emptyText }) {
  const list = items || [];
  return (
    <div className="product-identity-section" style={{ "--product-swatch": product?.hex || accent }} data-testid={`detail-col-${section}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: accent }} aria-hidden />
        <h4 className="font-heading text-base" style={{ color: "#1F1F1F", fontWeight: 600 }}>
          {label}
        </h4>
        {list.length > 0 && (
          <span
            className="ml-auto text-[11px] font-semibold rounded-full px-1.5 py-0.5"
            style={{ background: hexToRgba(accent, 0.12), color: "hsl(var(--foreground))" }}
          >
            {list.length}
          </span>
        )}
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground pl-4">{emptyText}</p>
      ) : (
        <div className="space-y-0.5">
          {list.map((it, idx) => (
            <NoteableItem
              key={it.key || idx}
              item={it}
              index={idx}
              numbered={numbered}
              accent={accent}
              section={section}
              product={product}
              allowExclude={Boolean(product)}
              showPills={showPills}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export const ProductDetailPanel = ({ selection, isHub }) => {
  const presenting = usePresentationMode();
  const reducedMotion = useReducedMotion();
  if (!selection) return null;
  const accent = selection.swatch || "#3F4F66";
  const product = isHub
    ? null
    : { slug: selection.slug, name: selection.display_name, hex: accent };

  const columns = isHub
    ? [
        {
          label: "What we've done",
          items: selection.done,
          accent,
          numbered: false,
          section: "hub-done",
          showPills: true,
          empty: "No recent milestones logged yet.",
        },
        {
          label: "What we're doing next",
          items: selection.next,
          accent,
          numbered: true,
          section: "hub-next",
          showPills: true,
          empty: "Nothing queued right now.",
        },
        {
          label: "Needs your attention",
          items: selection.attention,
          accent: "#B45309",
          numbered: true,
          section: "hub-attention",
          showPills: true,
          empty: "Nothing needs you right now.",
        },
      ]
    : [
        {
          label: "What we've done",
          items: selection.done,
          accent,
          numbered: false,
          section: "prod-done",
          showPills: false,
          empty: "No recent milestones for this product yet.",
        },
        {
          label: "What we're doing next",
          items: selection.next,
          accent,
          numbered: true,
          section: "prod-next",
          showPills: false,
          empty: "Nothing queued right now.",
        },
        {
          label: "What’s holding it up",
          items: selection.blocked,
          accent: "#B42318",
          numbered: false,
          section: "prod-blocked",
          showPills: true,
          empty: "Nothing is blocking this product.",
        },
      ];

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={isHub ? "hub" : selection.slug}
        initial={reducedMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reducedMotion ? { opacity: 1 } : { opacity: 0, y: -6 }}
        transition={{ duration: reducedMotion ? 0 : 0.22, ease: "easeOut" }}
        className="showcase-card product-identity-surface overflow-hidden"
        style={{ "--product-swatch": accent }}
        data-testid="product-detail-panel"
      >
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <p className="eyebrow text-muted-foreground">
                {isHub ? "The hub" : "Product"}
              </p>
              <h3
                className="font-heading text-2xl sm:text-3xl mt-0.5"
                style={{ color: "#1F1F1F", fontWeight: 600 }}
                data-testid="detail-title"
              >
                {selection.display_name}
              </h3>
              {selection.tagline && (
                <p className="text-sm text-muted-foreground mt-1 max-w-prose break-words [overflow-wrap:anywhere]" data-testid="detail-product-tagline">{selection.tagline}</p>
              )}
            </div>
            {!isHub && (
              <div className="shrink-0">
                <HealthPill health={selection.health} size="lg" />
              </div>
            )}
          </div>

          {!isHub && selection.one_line_status && (
            <p className="text-sm sm:text-base text-foreground mt-3 max-w-prose">
              <span className="font-semibold">Right now: </span>
              {selection.one_line_status}
            </p>
          )}
          {!isHub && selection.prod_state && (
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-prose">
              <span className="font-semibold">Live use: </span>
              {selection.prod_state}
            </p>
          )}
        </div>

        {!isHub && selection.content_notice && (
          <p className="px-5 sm:px-6 pb-4 text-sm text-muted-foreground" data-testid="detail-content-notice">{selection.content_notice}</p>
        )}
        <div className="border-t" style={{ borderColor: "hsl(var(--border))" }} />

        <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {columns.map((c) => (
            <Column
              key={c.section}
              label={c.label}
              items={c.items}
              accent={c.accent}
              numbered={c.numbered}
              section={c.section}
              product={product}
              showPills={c.showPills}
              emptyText={selection.empty_messages?.[c.section.replace("prod-", "")] || c.empty}
            />
          ))}
        </div>

        {!presenting && <div className="px-5 sm:px-6 pb-4" data-testid="detail-notes-tip">
          <p className="text-xs text-muted-foreground">Click a point to record feedback. X greys it out here and hides it in Presentation Mode; use Restore or Hidden items to recall it.</p>
        </div>}
      </motion.div>
    </AnimatePresence>
  );
};

export default ProductDetailPanel;
